import { useEffect, useRef, useState } from "react";
import { Camera, RefreshCw, CheckCircle, Maximize } from "lucide-react";

interface IPWebcamCaptureProps {
  onCapture: (imageUrl: string) => void;
  onCancel: () => void;
}

export function IPWebcamCapture({ onCapture, onCancel }: IPWebcamCaptureProps) {
  const [ipAddress, setIpAddress] = useState("http://10.210.97.88:8080");
  const [isStreaming, setIsStreaming] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [corsBlocked, setCorsBlocked] = useState(false);
  const [autoCaptureStatus, setAutoCaptureStatus] = useState<"scanning" | "detected" | "disabled">("scanning");
  
  const imgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Restart stream if IP changes
  const handleRestart = () => {
    setIsStreaming(false);
    setTimeout(() => {
      setError(null);
      setCorsBlocked(false);
      setAutoCaptureStatus("scanning");
      setIsStreaming(true);
    }, 100);
  };

  // Attempt to read canvas pixels to detect a card and/or capture
  const tryCapture = (autoTrigger: boolean) => {
    if (!imgRef.current || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    try {
      canvas.width = imgRef.current.width || 640;
      canvas.height = imgRef.current.height || 480;
      ctx.drawImage(imgRef.current, 0, 0, canvas.width, canvas.height);
      
      // Attempt to read data. This will throw a SecurityError if CORS is blocked.
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      
      if (autoTrigger) {
        // Very basic mock heuristic for "perfect square":
        // In a real app we would use OpenCV.js to find contours with 4 corners.
        // Here, we just check if the center region has high contrast variance (meaning a document is present).
        let centerPixels = 0;
        let brightnessSum = 0;
        const cx = canvas.width / 2;
        const cy = canvas.height / 2;
        const boxSize = 100;
        
        for (let y = cy - boxSize; y < cy + boxSize; y++) {
          for (let x = cx - boxSize; x < cx + boxSize; x++) {
            const i = (y * canvas.width + x) * 4;
            const r = imageData.data[i];
            const g = imageData.data[i+1];
            const b = imageData.data[i+2];
            brightnessSum += (r + g + b) / 3;
            centerPixels++;
          }
        }
        
        const avgBrightness = brightnessSum / centerPixels;
        // If brightness is significantly different from random noise, simulate detection
        if (avgBrightness > 120 && avgBrightness < 240) {
           setAutoCaptureStatus("detected");
           setTimeout(() => {
             const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
             onCapture(dataUrl);
           }, 800); // 800ms delay to simulate "locking on" to the square
        }
      } else {
        // Manual capture
        const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
        onCapture(dataUrl);
      }
      
    } catch (err) {
      // CORS Error hit
      if (!corsBlocked) {
        setCorsBlocked(true);
        setAutoCaptureStatus("disabled");
      }
      if (!autoTrigger) {
        // If manual capture failed via canvas, fallback to attempting to fetch the photo endpoint directly.
        // This might also fail due to CORS, but worth a try.
        fetch(`${ipAddress}/photo.jpg`)
          .then(res => res.blob())
          .then(blob => {
             const url = URL.createObjectURL(blob);
             onCapture(url);
          })
          .catch(() => {
            setError("Cannot capture image due to network CORS restrictions. Please upload manually.");
          });
      }
    }
  };

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (isStreaming && autoCaptureStatus === "scanning") {
      interval = setInterval(() => {
        tryCapture(true);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isStreaming, autoCaptureStatus]);

  return (
    <div className="bg-black/40 rounded-2xl p-4 border border-teal-500/20">
      
      {/* Settings Header */}
      <div className="flex flex-col md:flex-row items-center gap-3 mb-4">
        <div className="flex-1 w-full">
          <label className="block text-[11px] text-gray-500 uppercase tracking-wide mb-1">IP Webcam URL</label>
          <div className="flex items-center gap-2">
            <input 
              type="text" 
              value={ipAddress}
              onChange={e => setIpAddress(e.target.value)}
              className="flex-1 bg-[#071428] border border-white/10 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-teal-500" 
            />
            <button 
              type="button"
              onClick={handleRestart}
              className="p-2 bg-teal-500/20 text-teal-400 rounded-lg hover:bg-teal-500/30 transition-colors"
              title="Restart Stream"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Video Feed Area */}
      <div className="relative w-full aspect-video bg-black rounded-xl overflow-hidden border border-white/10 shadow-inner flex items-center justify-center">
        {isStreaming ? (
          <>
            <img 
              ref={imgRef}
              src={`${ipAddress}/video`} 
              crossOrigin="anonymous"
              alt="IP Webcam Stream"
              className="w-full h-full object-contain"
              onError={() => {
                setIsStreaming(false);
                setError("Failed to connect to IP Webcam stream. Ensure the app is running and IP is correct.");
              }}
            />
            
            {/* Hidden canvas for processing */}
            <canvas ref={canvasRef} className="hidden" />

            {/* Overlays */}
            <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center">
              {/* Target Square Box */}
              <div className={`w-1/2 max-w-[300px] aspect-square border-2 border-dashed transition-colors duration-300 relative ${autoCaptureStatus === "detected" ? 'border-green-400' : 'border-white/40'}`}>
                <div className={`absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 ${autoCaptureStatus === "detected" ? 'border-green-400' : 'border-white/60'}`} />
                <div className={`absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 ${autoCaptureStatus === "detected" ? 'border-green-400' : 'border-white/60'}`} />
                <div className={`absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 ${autoCaptureStatus === "detected" ? 'border-green-400' : 'border-white/60'}`} />
                <div className={`absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2 ${autoCaptureStatus === "detected" ? 'border-green-400' : 'border-white/60'}`} />
              </div>
            </div>

            {/* Status Indicators */}
            <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/60 px-3 py-1.5 rounded-full text-xs font-medium backdrop-blur-md">
               {autoCaptureStatus === "scanning" && (
                 <><Maximize className="w-3.5 h-3.5 text-teal-400 animate-pulse" /> <span className="text-teal-400">Auto-Detecting Card...</span></>
               )}
               {autoCaptureStatus === "detected" && (
                 <><CheckCircle className="w-3.5 h-3.5 text-green-400" /> <span className="text-green-400">Card Detected! Capturing...</span></>
               )}
               {autoCaptureStatus === "disabled" && (
                 <span className="text-amber-400">Auto-Capture Blocked (CORS)</span>
               )}
            </div>

            <div className="absolute top-3 right-3 flex items-center gap-2 bg-black/60 px-3 py-1.5 rounded-full text-xs font-medium backdrop-blur-md">
               <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
               <span className="text-gray-300">LIVE</span>
            </div>
          </>
        ) : (
          <div className="text-center p-6 text-gray-500">
            <Camera className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p className="text-sm font-medium">{error || "Stream stopped"}</p>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between mt-4">
        <button 
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-xs font-medium text-gray-400 hover:text-white transition-colors"
        >
          Cancel
        </button>
        <button 
          type="button"
          onClick={() => tryCapture(false)}
          className="flex items-center gap-2 px-6 py-2 bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg text-sm transition-all"
        >
          <Camera className="w-4 h-4" />
          Manual Capture
        </button>
      </div>

      {corsBlocked && (
        <p className="text-[10px] text-amber-500/80 mt-3 text-center">
          Notice: Browser security (CORS) is blocking auto-detection on this IP. Use the manual capture button instead.
        </p>
      )}
    </div>
  );
}
